/* eslint-disable no-console */
import axios from 'axios'
import { GA_TRACKING_ID, GTAG_EVENT_TYPE, PLAUSIBLE_DOMAIN } from '../config'
import formatGa4Data from '../utils/formatGa4Data'

export const state = () => ({
  gaTrackingId: GA_TRACKING_ID,
})

const getDigestHex = async (message) => {
  const msgUint8 = new TextEncoder().encode(message)
  const hashBuffer = await window.crypto.subtle.digest('SHA-256', msgUint8)
  const hashArray = Array.from(new Uint8Array(hashBuffer))
  const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  return hashHex
}
// eslint-disable-next-line no-unused-vars
const getUserData = async (userData) => {
  if (!userData) return {}

  const result = {}

  const email = formatGa4Data.normalizeEmail(userData.email)
  if (email) {
    const emailHash = await getDigestHex(email)
    result.sha256_email_address = [emailHash]
  }

  const phone = formatGa4Data.normalizePhone(userData.phone_number)
  if (phone) {
    const phoneHash = await getDigestHex(phone)
    result.sha256_phone_number = [phoneHash]
  }

  if (userData.ship_address || userData.first_name || userData.last_name) {
    const address = {}

    const firstName = formatGa4Data.normalizePersonName(userData.first_name)
    if (firstName) {
      const firstNameHash = await getDigestHex(firstName)
      address.sha256_first_name = firstNameHash
    }

    const lastName = formatGa4Data.normalizePersonName(userData.last_name)
    if (lastName) {
      const lastNameHash = await getDigestHex(lastName)
      address.sha256_last_name = lastNameHash
    }

    const street = formatGa4Data.normalizeAddress(
      userData.ship_address?.address1
    )
    if (street) {
      const streetHash = await getDigestHex(street)
      address.sha256_street = streetHash
    }

    const city = formatGa4Data.normalizeCity(userData.ship_address?.city)
    if (city) {
      address.city = city
    }

    const region = formatGa4Data.normalizeCity(
      // eslint-disable-next-line camelcase
      userData.ship_address?.state_name
    )
    if (region) {
      address.region = region
    }

    const postalCode = formatGa4Data.normalizePostalCode(
      userData.ship_address?.zipcode
    )
    if (postalCode) {
      address.postal_code = postalCode
    }

    const country = userData.country_id || userData.country_name
    if (country) {
      address.country = country.toUpperCase()
    }

    if (Object.keys(address).length > 0) {
      result.address = [address]
    }
  }

  return result
}

export const getters = {
  getGtagItem: (state) => (product, quantity) => {
    const item = {
      item_name: product.name,
      item_id: `${product.id}`,
      item_variant: product.variant_id ? `${product.variant_id}` : undefined,
      // https://developers.google.com/analytics/devguides/collection/ga4/item-scoped-ecommerce#add_an_item-scoped_custom_parameter
      item_sku: product.sku,
      item_case_device: product.case_device_id,
    }
    if (product.price) {
      item.price = Math.round(Number(product.price) * 100) / 100
    }
    if (typeof quantity === 'number') {
      item.quantity = quantity
    } else if (typeof product.quantity === 'number') {
      item.quantity = product.quantity
    } else {
      item.quantity = 1
    }
    if (typeof product.category === 'string') {
      const categories = product.category.split('/')
      for (let i = 0; i < categories.length; i++) {
        const categoryName = categories[i]
        if (i === 0) {
          item.item_category = categoryName
        } else {
          item[`item_category${i + 1}`] = categoryName
          if (i === 4) break
        }
      }
    }
    return item
  },
}

export const actions = {
  async emitGtagEvent(
    { state: { gaTrackingId }, getters: { getGtagItem }, rootState },
    { name, products, params = {}, paramsToHash }
  ) {
    if (!process.client) return
    if (paramsToHash) {
      const hashings = Object.keys(paramsToHash).map(async (key) => {
        const value = paramsToHash[key]
        if (value === undefined) return
        try {
          params[`${key}_hash`] = await getDigestHex(value)
        } catch (err) {
          console.error(err)
        }
      })
      await Promise.all(hashings)
    }
    if (Array.isArray(products)) {
      params.items = []
      products.forEach((product) => {
        params.items.push(getGtagItem(product))
      })
    }
    if (globalThis.EXPERIMENT_ID) {
      params.exp_id = globalThis.EXPERIMENT_ID
    }
    if (globalThis.CART_MODE) {
      params.cart_mode = globalThis.CART_MODE
    }
    if (rootState.user?.id) {
      params.user_id = String(rootState.user.id)
    }
    const timestamp = Date.now()
    try {
      const data = {
        type: GTAG_EVENT_TYPE,
        // @TODO: ...getAnalyticsContext(),
        event: {
          name,
          params,
          _unhashed_data: paramsToHash,
        },
        event_id: `${name}.${timestamp}`,
        timestamp,
      }
      window.postMessage(data, window.origin)
      // @TODO: `watchGtagEvents`
    } catch (err) {
      console.error(err)
    }
    if (!window.gtag && Array.isArray(window.dataLayer)) {
      window.gtag = function () {
        window.dataLayer.push(arguments)
      }
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', name, {
        send_to: gaTrackingId,
        ...params,
      })
    }
    if (PLAUSIBLE_DOMAIN) {
      try {
        axios.post('https://plausible.io/api/event', {
          name,
          url: window.location.href,
          domain: PLAUSIBLE_DOMAIN,
          props: params,
        })
      } catch {}
    }
  },
}
